import { TabulatorFull } from "tabulator-tables";



import type { IReportArticleRow } from "@/data/model/ReportArticleRow";
import type { IReportCategoryRow } from "@/data/model/ReportCategoryRow";
import type { IReportDetailRow } from "@/data/model/ReportDetailRow";
import type { ISalesReportData } from "@/data/model/SalesReportData";
import type { ISalesReportTotalRow } from "@/data/model/SalesReportTotalRow";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { replaceString } from "@/data/util/String";



import { toMoney } from "@/shared/utils/format";



import { OperatorClient } from "@/operator/OperatorClient";
import { ProductionContext } from "@/operator/context/ProductionContext";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { UserMenu } from "@/operator/ui/UserMenu";
import { FulfillmentSoundNotifications } from "@/operator/ux/SoundNotifications";



import { BaseController, ViewTransition } from "./BaseController";


const totalExclTipByVatRateCode = "TotalExclTipByVatRate";

class TableRow {
    category: string;
    netQuantity: string;
    vatPercent: string;
    cancellationQuantity: string;
    grossNetSales: string;
    grossCancellations: string;
    discountQuantity: string;
    grossSalesDiscounts: string;
    _children?: TableRow[];

    constructor(
        category: string,
        netQuantity: string,
        vatPercent: string,
        cancellationQuantity: string,
        grossNetSales: string,
        grossCancellations: string,
        discountQuantity: string,
        grossSalesDiscounts: string,
        _children?: TableRow[]
    ) {
        this.category = category;
        this.netQuantity = netQuantity;
        this.vatPercent = vatPercent;
        this.cancellationQuantity = cancellationQuantity;
        this.grossNetSales = grossNetSales;
        this.grossCancellations = grossCancellations;
        this.discountQuantity = discountQuantity;
        this.grossSalesDiscounts = grossSalesDiscounts;
        this._children = _children;
    }
}
export class SalesReportController extends BaseController {
    private productionContext: ProductionContext;

    private readonly $head: JQuery;
    private readonly $scrollable: JQuery;
    private readonly $dateFilter: JQuery;
    private readonly $headerFilter: JQuery;
    private data: TableRow[] = [];
    private table?: any;

    constructor() {
        super("salesreport");

        this.productionContext = ProductionContext.getInstance();

        this.$head = $('<div class="head"><div class="logo">tastier</div></div>').appendTo(this.$view);
        new UserMenu().appendTo(this.$head);

        this.$scrollable = $('<div class="scrollable"></div>').appendTo(this.$view);
        this.$dateFilter = $('<div style="padding: 12px;"></div>').appendTo(this.$scrollable);

        this.$headerFilter = $(
            '<div class="font-weight-normal" style="padding-left: 12px; padding-bottom: 12px;"></div>'
        ).appendTo(this.$scrollable);
        $('<div id="salesreport-table"></div>').appendTo(this.$scrollable);
    }

    public showView(data: ISalesReportData, transition?: ViewTransition) {
        this.data = this.ConvertCategoriesToRows(data.data)
            .concat(this.GenerateEmptyRow())
            .concat(this.ConvertTotalsToRows(data.totals));

        if (!this.isVisible()) {
            this.show(transition);
        }

        const now = new Date();
        this.$dateFilter.empty();
        this.$headerFilter.empty();
        $(
            '<div class="font-weight-normal">' +
                replaceString(
                    trans.salesReports.dateRangeGenerated,
                    DateTime.toPreciseDateTimeString(now),
                    DateTime.toPreciseDateTimeString(data.filters.startDate),
                    DateTime.toPreciseDateTimeString(data.filters.endDate)
                ) +
                "</div>"
        ).appendTo(this.$dateFilter);

        let filterText = "";
        if (data.filters.userName != null && data.filters.userName != "") {
            filterText += trans.general.entities.users + " <b>" + data.filters.userName + "</b>&ensp;&ensp;";
        }

        if (data.filters.filterByEndUsers) {
            filterText += "<b>" + trans.salesReports.endUsers + "</b>&ensp;&ensp;";
        }

        if (data.filters.rooms != null && data.filters.rooms.length > 0) {
            filterText +=
                trans.general.entities.rooms +
                " <b>" +
                data.filters.rooms.map((r) => r.name).join(", ") +
                "</b>&ensp;&ensp;";
        }

        if (data.filters.offers != null && data.filters.offers.length > 0) {
            filterText +=
                trans.general.entities.offers +
                " <b>" +
                data.filters.offers.map((r) => r.name).join(", ") +
                "</b>&ensp;&ensp;";
        }

        if (data.filters.fulfillmentGroups != null && data.filters.fulfillmentGroups.length > 0) {
            filterText +=
                trans.general.entities.fulfillmentGroups +
                " <b>" +
                data.filters.fulfillmentGroups
                    .map((r) => Format.translate(r.name, OperatorClient.context.settings?.location?.language!))
                    .join(", ") +
                "</b>&ensp;&ensp;";
        }

        this.$headerFilter.html(filterText);

        this.initializeTable();
    }

    protected onBecomeVisible() {
        operatorEvents.productionContext.FulfillmentAdded.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
    }

    protected onBecomeInvisible() {
        operatorEvents.productionContext.FulfillmentAdded.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);

    }

    private ConvertDetailsToRows(categoryVatPercent: string, rows: IReportDetailRow[]): TableRow[] {
        return rows.map((r) => {
            const children = this.ConvertDetailsToRows(categoryVatPercent, r.children);
            return new TableRow(
                r.name,
                r.netQuantity.toString(),
                categoryVatPercent,
                (r.cancellationQuantity * -1).toString(),
                toMoney(r.grossNetSales, true),
                toMoney(r.grossCancellation * -1, true),
                r.discountQuantity.toString(),
                toMoney(r.grossSalesDiscounts, true),
                children.length == 0 ? undefined : children
            );
        });
    }

    private ConvertArticlesToRows(categoryVatPercent: string, articles: IReportArticleRow[]): TableRow[] {
        return articles.map((a) => {
            return new TableRow(
                a.name,
                a.netQuantity.toString(),
                categoryVatPercent,
                (a.cancellationQuantity * -1).toString(),
                toMoney(a.grossNetSales, true),
                toMoney(a.grossCancellation * -1, true),
                a.discountQuantity.toString(),
                toMoney(a.grossSalesDiscounts, true),
                this.ConvertDetailsToRows(categoryVatPercent, a.details)
            );
        });
    }

    private ConvertTotalsToRows(totals: ISalesReportTotalRow[]): TableRow[] {
        return totals.flatMap((t, i) => {
            const isVatRateRow = t.code === totalExclTipByVatRateCode;
            const row = new TableRow(
                trans.enums.salesOrderSummary[isVatRateRow ? "TotalExclTip" : t.code],
                t.netQuantity?.toString() ?? "",
                t.vatPercent,
                t.cancellationQuantity?.toString() ?? "",
                toMoney(t.grossNetSales, true),
                toMoney(t.grossCancellation, true),
                t.discountQuantity?.toString() ?? "",
                toMoney(t.grossSalesDiscounts, true),
                undefined
            );

            // the totals (excl. tip) per VAT rate get a blank row above and below
            const wasVatRateRow = i > 0 && totals[i - 1].code === totalExclTipByVatRateCode;
            return i > 0 && isVatRateRow !== wasVatRateRow ? [this.GenerateEmptyRow(), row] : [row];
        });
    }

    private ConvertCategoriesToRows(articles: IReportCategoryRow[]): TableRow[] {
        return articles.map((c) => {
            const vatPercent = (c.vat * 100).toFixed(2) + "%";
            return new TableRow(
                c.name,
                c.netQuantity.toString(),
                vatPercent,
                (c.cancellationQuantity * -1).toString(),
                toMoney(c.grossNetSales, true),
                toMoney(c.grossCancellation * -1, true),
                c.discountQuantity.toString(),
                toMoney(c.grossSalesDiscounts, true),
                this.ConvertArticlesToRows(vatPercent, c.articles)
            );
        });
    }

    private GenerateEmptyRow(): TableRow {
        return new TableRow("", "", "", "", "", "", "", "", undefined);
    }

    private initializeTable() {
        if (this.table === undefined) {
            this.table = new TabulatorFull("#salesreport-table", {
                maxHeight: "100%",
                height: "100%",
                data: this.data,
                selectable: true,
                dataTree: true,
                layout: "fitData",
                dataTreeCollapseElement: "<i style='width: 24px;' class='fas fa-minus-square'></i>",
                dataTreeExpandElement: "<i style='width: 24px;' class='fas fa-plus-square'></i>",
                renderHorizontal: "virtual",
                dataTreeChildIndent: 20,
                dataTreeStartExpanded: false,
                columns: [
                    {
                        title: trans.salesReports.reportCategory,
                        field: "category",
                        resizable: "header",
                        width: 240,
                        sorter: "string",
                        headerSort: false,
                        frozen: true,
                    },
                    {
                        title: trans.salesReports.netQuantity,
                        field: "netQuantity",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.salesReports.cancellationQuantity,
                        field: "cancellationQuantity",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.salesReports.discountQuantity,
                        field: "discountQuantity",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.salesReports.grossNetSales,
                        field: "grossNetSales",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.salesReports.grossCancellations,
                        field: "grossCancellations",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.salesReports.grossSalesDiscounts,
                        field: "grossSalesDiscounts",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                    {
                        title: trans.salesReports.vatPercent,
                        field: "vatPercent",
                        resizable: "header",
                        headerSort: false,
                        hozAlign: "right",
                    },
                ],
            });
        } else {
            this.table.setData(this.data);
        }
    }
}
